import React from 'react';
import { COHORT_SEASONS } from '../../utils/calendar';
import { SegmentedControl } from '../ui/SegmentedControl';
import { PendingNote } from '../ui/PendingNote';
import { MetricInfoButton } from '../definitions/MetricInfoButton';
export type RetentionMode = 'season' | 'yoy';
export type YoyWindow = 'sameSeason' | 'within12';
interface RetentionControlsProps {
  mode: RetentionMode;
  onModeChange: (m: RetentionMode) => void;
  base: number;
  onBaseChange: (v: number) => void;
  comp: number;
  onCompChange: (v: number) => void;
  yoyWindow: YoyWindow;
  onYoyWindowChange: (w: YoyWindow) => void;
}
const selectClass = 'mt-1 h-9 w-full rounded-md border border-line bg-white px-2.5 text-[13px] font-medium text-ink focus:border-navy/50 focus:outline-none focus:ring-2 focus:ring-navy/15';
const seasonLabel = (idx: number) => `${COHORT_SEASONS[idx].label}${COHORT_SEASONS[idx].inProgress ? ' (in progress)' : ''}`;
export function RetentionControls(p: RetentionControlsProps) {
  const baseOptions = p.mode === 'season' ? COHORT_SEASONS.slice(0, -1) : COHORT_SEASONS.filter(s => s.idx + 4 < COHORT_SEASONS.length);
  const compOptions = COHORT_SEASONS.filter(s => s.idx > p.base);
  return <div className="rounded-lg border border-line bg-white px-5 py-4" data-id="element-466" data-mp="23dy91sgw4lnp">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4" data-id="element-467" data-mp="1ye3ijq111y36">
        <div data-id="element-468" data-mp="1yfwqr9ragqda">
          <div className="flex items-center gap-1.5" data-id="element-469" data-mp="1yg6eeqov2lft">
            <h2 className="text-[15px] font-semibold text-navy" data-id="element-470" data-mp="2v2t0thgkgspo">Season-Based Retention</h2>
            <MetricInfoButton id={p.mode === 'season' ? 'seasonRetention' : 'yoyRetention'} label="Season-Based Retention" data-id="element-471" data-mp="2r3k72clj0uu4" />
          </div>
          <div className="mt-2" data-id="element-472" data-mp="2m9oanekwvg12">
            <SegmentedControl ariaLabel="Retention type" value={p.mode} onChange={p.onModeChange} options={[{
            value: 'season',
            label: 'Season retention'
          }, {
            value: 'yoy',
            label: 'YOY retention'
          }]} data-id="element-473" data-mp="2hd7nonpg77pk" />
          </div>
        </div>

        <label className="w-48 text-xs font-medium text-ink-muted" data-id="element-474" data-mp="3sydrlhl7x6kd">
          Base season
          <select value={p.base} onChange={e => p.onBaseChange(Number(e.target.value))} className={selectClass} data-id="element-475" data-mp="1qsvz68kdfr5">
            {baseOptions.map(s => <option key={s.idx} value={s.idx} data-id="element-476" data-mp="3o6n9uqjybgix">{seasonLabel(s.idx)}</option>)}
          </select>
        </label>
        <span className="pb-2.5 text-ink-subtle" aria-hidden data-id="element-477" data-mp="3a62rfvhy7mpy">→</span>

        {p.mode === 'season' ? <label className="w-52 text-xs font-medium text-ink-muted" data-id="element-478" data-mp="3f709m82ykgrg">
            Comparison season <span className="font-normal text-ink-subtle" data-id="element-479" data-mp="317hecfv8wzwk">(default: next)</span>
            <select value={p.comp} onChange={e => p.onCompChange(Number(e.target.value))} className={selectClass} data-id="element-480" data-mp="357hr7w2p531i">
              {compOptions.map(s => <option key={s.idx} value={s.idx} data-id="element-481" data-mp="zcykcbknfq4s">{seasonLabel(s.idx)}</option>)}
            </select>
          </label> : <label className="w-64 text-xs font-medium text-ink-muted" data-id="element-482" data-mp="2ny71xybzqs8m">
            Comparison period
            <select value={p.yoyWindow} onChange={e => p.onYoyWindowChange(e.target.value as YoyWindow)} className={selectClass} data-id="element-483" data-mp="2jxwnrye1ovio">
              <option value="within12" data-id="element-484" data-mp="2xq32dg1juug0">Any qualifying program within 12 months (primary)</option>
              <option value="sameSeason" data-id="element-485" data-mp="2sw8owucjfbpd">{seasonLabel(p.base + 4)} · same season next year (optional)</option>
            </select>
          </label>}

        <div className="ml-auto max-w-[360px] pb-1.5 text-[11.5px] leading-relaxed text-ink-muted" data-id="element-486" data-mp="32o4n05emj1tl">
          Uses the season and year of the <b className="text-ink" data-id="element-487" data-mp="3gm31dasy2y93">registered program</b>, not the purchase date. Counts distinct <b className="text-ink" data-id="element-488" data-mp="3bpcjqj1hrulk">PlayerIDs</b>, not MemberIDs or registration rows. The acquisition date range does not apply here.
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-489" data-mp="1m9lhmd7beg97">
            <PendingNote data-id="element-490" data-mp="1hb1wgf0jp8hw">PlayerID & program season fields pending technical validation</PendingNote>
            {p.mode === 'yoy' ? <PendingNote data-id="element-491" data-mp="1jonjmz4r504h">YOY default window pending Jeremy</PendingNote> : <PendingNote data-id="element-492" data-mp="1noxou9kpzmia">Next-season vs any-later-season rule pending Jeremy</PendingNote>}
          </div>
        </div>
      </div>
    </div>;
}